import { FormEvent, Fragment, useCallback, useEffect, useMemo, useState } from "react";

import {
  fetchBandsintownDigest,
  fetchBandsintownHistory,
  sendBandsintownDigest,
  type BandsintownHistoryEntry,
} from "@/api/crm";
import type { BandsintownDigestVenue } from "@/types/crm";
import { formatDate } from "@/lib/crmUtils";
import { useToast } from "@/components/ToastProvider";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/pricing/LockedFeatureCard";
import { useCapabilities } from "@/hooks/useCapabilities";
type VenueDigestorOverride = {
  contact_name?: string;
  email?: string;
  phone?: string;
  website?: string;
  notes?: string;
};

const parseTagList = (value: string) =>
  value
    .split(/[\s,]+/)
    .map((token) => token.trim())
    .filter(Boolean);

const InputField = ({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  type?: "text" | "email" | "tel" | "url";
}) => (
  <label className="text-xs text-muted-foreground">
    {label}
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-sm text-foreground"
    />
  </label>
);

const HISTORY_KEY = "crm:bandsintownDigestHistory";
const DIGEST_STORAGE_KEY = "crm:bandsintownDigestState";
const SELECTION_STORAGE_KEY = "crm:bandsintownDigestSelection";
const BANDSINTOWN_SLUG_REGEX = /[^a-zA-Z0-9]/g;

const parseIdentifiers = (value: string): string[] => {
  return value
    .split(/[\n,]+/)
    .map((token) => token.trim())
    .filter(Boolean);
};

const normalizeBandsintownSlug = (value: string): string => {
  return (value ?? "")
    .replace(BANDSINTOWN_SLUG_REGEX, "")
    .toLowerCase()
    .trim();
};

const mergeHistoryEntries = (incoming: string[], existing: string[], limit = 50): string[] => {
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const raw of [...incoming, ...existing]) {
    const value = raw?.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    merged.push(value);
    if (merged.length >= limit) break;
  }
  return merged;
};

type DigestState = {
  loading: boolean;
  error: string | null;
  slug: string | null;
  venues: BandsintownDigestVenue[];
  serverErrors: string[];
};

const initialDigestState: DigestState = {
  loading: false,
  error: null,
  slug: null,
  venues: [],
  serverErrors: [],
};

type CapabilityBlockedError = {
  code?: string;
  status?: number;
  capability?: string;
  payload?: { capability?: string; error?: string } | null;
};

function isCapabilityBlockedError(error: unknown): error is CapabilityBlockedError {
  if (!error || typeof error !== "object") return false;
  if ((error as CapabilityBlockedError).code === "capability_required") return true;
  if ((error as CapabilityBlockedError).status === 403) {
    const payload = (error as CapabilityBlockedError).payload;
    if (payload && payload.error === "requires_pro") return true;
  }
  return false;
}

function capabilityFromError(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  const direct = (error as CapabilityBlockedError).capability;
  if (typeof direct === "string" && direct) return direct;
  const payload = (error as CapabilityBlockedError).payload;
  if (payload && typeof payload.capability === "string" && payload.capability) return payload.capability;
  return null;
}

export function BandsintownDigestPanel() {
  const { capabilities, openUpgradeModal } = useCapabilities();
  const canUseCrmIngestion = Boolean(
    capabilities.canUseBandsintownAutomation ?? capabilities.canUseCrmIngestion,
  );
  const [bandsintownId, setBandsintownId] = useState("");
  const windowFilter: "upcoming" | "past" | "all" = "all";
  const [history, setHistory] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(HISTORY_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map((item: unknown) => String(item)).filter(Boolean) : [];
    } catch {
      return [];
    }
  });
  const [selectedHistory, setSelectedHistory] = useState<string[]>([]);
  const [serverHistory, setServerHistory] = useState<BandsintownHistoryEntry[]>([]);
  const [state, setState] = useState<DigestState>(initialDigestState);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectedDigestId, setSelectedDigestId] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, VenueDigestorOverride>>({});
  const [tags, setTags] = useState<string[]>(["bandsintown", "digest"]);
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<{ success?: string; error?: string }>({});
  const { push } = useToast();
  const handleCapabilityBlock = useCallback(
    (error: unknown, fallback?: string) => {
      if (!isCapabilityBlockedError(error)) return;
      const capability = capabilityFromError(error);
      openUpgradeModal(capability ?? fallback ?? "CRM ingestion");
    },
    [openUpgradeModal],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(DIGEST_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.slug === "string" && Array.isArray(parsed.venues)) {
          setState({
            loading: false,
            error: null,
            slug: parsed.slug,
            venues: parsed.venues as BandsintownDigestVenue[],
            serverErrors: Array.isArray(parsed.serverErrors) ? parsed.serverErrors : [],
          });
        }
      }
      const selectionRaw = window.localStorage.getItem(SELECTION_STORAGE_KEY);
      if (selectionRaw) {
        const parsedSelection = JSON.parse(selectionRaw);
        if (Array.isArray(parsedSelection)) {
          setSelectedRows(new Set(parsedSelection.map((id: unknown) => String(id))));
        }
      }
    } catch {
      // ignore hydration failures
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 50)));
    } catch {
      // ignore localStorage errors
    }
  }, [history]);

  useEffect(() => {
    if (!canUseCrmIngestion) return;
    let cancelled = false;
    (async () => {
      try {
        const entries = await fetchBandsintownHistory();
        if (cancelled) {
          return;
        }
        setServerHistory(entries);
        setHistory((prev) => mergeHistoryEntries(entries.map((entry) => entry.artist_id), prev));
      } catch (error) {
        handleCapabilityBlock(error, "CRM ingestion");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canUseCrmIngestion, handleCapabilityBlock]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (!state.slug && state.venues.length === 0) {
        window.localStorage.removeItem(DIGEST_STORAGE_KEY);
      } else {
        window.localStorage.setItem(
          DIGEST_STORAGE_KEY,
          JSON.stringify({
            slug: state.slug,
            venues: state.venues,
            serverErrors: state.serverErrors,
          }),
        );
      }
    } catch {
      // ignore persistence failures
    }
  }, [state.slug, state.venues, state.serverErrors]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const ids = Array.from(selectedRows);
      if (ids.length) {
        window.localStorage.setItem(SELECTION_STORAGE_KEY, JSON.stringify(ids));
      } else {
        window.localStorage.removeItem(SELECTION_STORAGE_KEY);
      }
    } catch {
      // ignore persistence failures
    }
  }, [selectedRows]);

  useEffect(() => {
    if (state.venues.length === 0) {
      setSelectedDigestId(null);
      return;
    }
    if (!selectedDigestId || !state.venues.some((venue) => venue.digest_id === selectedDigestId)) {
      setSelectedDigestId(state.venues[0].digest_id);
    }
  }, [state.venues, selectedDigestId]);

  const artistSlug = state.slug;
  const activeVenue = selectedDigestId
    ? state.venues.find((venue) => venue.digest_id === selectedDigestId) ?? null
    : null;
  const handleTagChange = (value: string) => {
    setTags(parseTagList(value));
  };
  const setOverrideField = (
    digestId: string,
    field: keyof VenueDigestorOverride,
    value: string,
  ) => {
    setOverrides((prev) => {
      const current = prev[digestId] ?? {};
      return {
        ...prev,
        [digestId]: {
          ...current,
          [field]: value,
        },
      };
    });
  };

  const handleFetch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canUseCrmIngestion) {
      openUpgradeModal("Bandsintown automation");
      return;
    }
    const ids = parseIdentifiers(bandsintownId);
    const normalizedSlugs = ids.map((token) => normalizeBandsintownSlug(token)).filter(Boolean);
    const slug = normalizedSlugs[0];
    if (!slug) {
      setState((prev) => ({ ...prev, error: "Enter at least one Bandsintown ID or slug." }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null, serverErrors: [] }));
    setSelectedRows(new Set());

    try {
      const response = await fetchBandsintownDigest({ bandsintown_id: slug, window: windowFilter });
      setState({
        loading: false,
        error: null,
        slug: response.slug,
        venues: response.venues,
        serverErrors: response.errors ?? [],
      });
      setSelectedRows(new Set(response.venues.map((venue) => venue.digest_id)));
      setHistory((prev) => mergeHistoryEntries([slug], prev));
      if (!response.venues.length) {
        push({ title: "Bandsintown digest", message: "No venues found for that ID." });
      } else {
        await sendVenuesToCrm(response.slug, response.venues);
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Unable to fetch Bandsintown data.",
      }));
      handleCapabilityBlock(error, "CRM ingestion");
    }
  };

  const toggleSelection = (digestId: string) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(digestId)) {
        next.delete(digestId);
      } else {
        next.add(digestId);
      }
      return next;
    });
  };

  const allSelected = useMemo(() => {
    return state.venues.length > 0 && state.venues.every((venue) => selectedRows.has(venue.digest_id));
  }, [selectedRows, state.venues]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRows(new Set());
      return;
    }
    setSelectedRows(new Set(state.venues.map((venue) => venue.digest_id)));
  };

  const selectedVenues = useMemo(
    () => state.venues.filter((venue) => selectedRows.has(venue.digest_id)),
    [selectedRows, state.venues],
  );

  const serverHistoryMap = useMemo(() => {
    const map = new Map<string, BandsintownHistoryEntry>();
    serverHistory.forEach((entry) => {
      if (entry.artist_id) {
        map.set(entry.artist_id, entry);
      }
    });
    return map;
  }, [serverHistory]);

  const resolveEventDate = (venue: BandsintownDigestVenue) => {
    const upcomingEvent = venue.events.find((event) => !event.is_past && event.event_date);
    if (upcomingEvent?.event_date) {
      return upcomingEvent.event_date;
    }
    const fallbackEvent = venue.events.find((event) => event.event_date);
    return fallbackEvent?.event_date ?? null;
  };

  const handleDismissVenue = useCallback(
    (digestId: string) => {
      setState((prev) => ({
        ...prev,
        venues: prev.venues.filter((venue) => venue.digest_id !== digestId),
      }));
      setSelectedRows((prev) => {
        const next = new Set(prev);
        next.delete(digestId);
        return next;
      });
      push({ title: "Bandsintown digest", message: "Venue dismissed from this digest." });
    },
    [push],
  );

  const sendVenuesToCrm = useCallback(
    async (slugToSend: string, venuesToSend: BandsintownDigestVenue[]) => {
      const normalizedSlug = slugToSend?.trim();
      if (!normalizedSlug) {
        setState((prev) => ({ ...prev, error: "Run a digest before sending to CRM." }));
        return;
      }
      if (venuesToSend.length === 0) {
        setState((prev) => ({ ...prev, error: "Select at least one venue to send to CRM." }));
        return;
      }
      setSendStatus({});
      setSending(true);
      setState((prev) => ({ ...prev, error: null }));

      try {
        const normalizedTags = tags.length ? tags : ["bandsintown", "digest"];
        const payload = venuesToSend.map((venue) => {
          const venueOverrides = overrides[venue.digest_id];
          const hasOverrides = venueOverrides && Object.keys(venueOverrides).length > 0;
          return {
            digest_id: venue.digest_id,
            venue_name: venue.venue_name,
            city: venue.city,
            state: venue.state,
            country: venue.country,
            ticket_url: venue.ticket_url,
            event_date: resolveEventDate(venue),
            overrides: hasOverrides ? venueOverrides : undefined,
          };
        });
        const results = await sendBandsintownDigest({
          slug: normalizedSlug,
          venues: payload,
          tags: normalizedTags,
        });
        const successes = results.filter((entry) => entry.status !== "failed").length;
        const failures = results.length - successes;
        if (successes) {
          push({
            title: "CRM",
            message: `${successes} venue${successes === 1 ? "" : "s"} synced from Bandsintown.`,
          });
          setSendStatus({
            success: `${successes} venue${successes === 1 ? "" : "s"} synced from Bandsintown.`,
          });
        }
        if (failures) {
          push({
            title: "CRM",
            message: `${failures} venue${failures === 1 ? "" : "s"} failed to sync.`,
          });
          if (!successes) {
            setSendStatus({
              error: `${failures} venue${failures === 1 ? "" : "s"} failed to sync.`,
            });
          }
        }
        if (successes) {
          const successfulIds = new Set(
            results.filter((entry) => entry.status !== "failed").map((entry) => entry.digest_id),
          );
          const remainingVenues = state.venues.filter((venue) => !successfulIds.has(venue.digest_id));
          setState((prev) => ({
            ...prev,
            venues: prev.venues.filter((venue) => !successfulIds.has(venue.digest_id)),
          }));
          setSelectedRows((prev) => {
            const updated = new Set(prev);
            successfulIds.forEach((id) => updated.delete(id));
            return updated;
          });
          setOverrides((prev) => {
            const next = { ...prev };
            successfulIds.forEach((id) => {
              delete next[id];
            });
            return next;
          });
          setSelectedDigestId((prev) => {
            if (prev && successfulIds.has(prev)) {
              return remainingVenues[0]?.digest_id ?? null;
            }
            return prev;
          });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Unable to sync venues.";
        push({ title: "CRM", message });
        setSendStatus({ error: message });
        handleCapabilityBlock(error, "CRM ingestion");
      } finally {
        setSending(false);
      }
    },
    [handleCapabilityBlock, push, overrides, tags, state.venues],
  );

  const handleSend = () => {
    if (!canUseCrmIngestion) {
      openUpgradeModal("Bandsintown automation");
      return;
    }
    if (!artistSlug) {
      setState((prev) => ({ ...prev, error: "Run a digest before sending to CRM." }));
      return;
    }
    void sendVenuesToCrm(artistSlug, selectedVenues);
  };

  const handleHistorySelection = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selections = Array.from(event.target.selectedOptions).map((option) => option.value);
    setSelectedHistory(selections);
  };

  const handleAddFromHistory = () => {
    if (!selectedHistory.length) return;
    const normalizedCurrent = parseIdentifiers(bandsintownId)
      .map((token) => normalizeBandsintownSlug(token))
      .filter(Boolean);
    const merged = Array.from(new Set([...normalizedCurrent, ...selectedHistory]));
    setBandsintownId(merged.join("\n"));
    setSelectedHistory([]);
  };

  const runDisabled = state.loading || !bandsintownId.trim();

  return (
    <div className="space-y-5 rounded-2xl border border-border/60 bg-background/40 p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Bandsintown Digest</h2>
          <p className="text-sm text-muted-foreground">
            Scrape a Bandsintown artist slug and fan out unique venues to review + push into CRM.
          </p>
        </div>
      </div>

      <CapabilityGate
        flag={["canUseCrmIngestion", "canUseBandsintownAutomation"]}
        fallback={
          <LockedFeatureCard
            flag="canUseCrmIngestion"
            title="Bandsintown automation"
            description="Upgrade to fetch and sync Bandsintown venues automatically."
            onUpgrade={() => openUpgradeModal("Bandsintown automation")}
          />
        }
      >
        <div className="space-y-5">
          <form onSubmit={handleFetch} className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium text-muted-foreground">
              Bandsintown IDs or slugs
              <textarea
                className="mt-2 h-28 w-full rounded-xl border border-border/70 bg-background/70 p-3 text-sm text-foreground shadow-inner"
                placeholder="ex: caltucky, 124243"
                value={bandsintownId}
                onChange={(event) => setBandsintownId(event.target.value)}
                disabled={state.loading}
              />
            </label>

            {history.length > 0 && (
              <div className="rounded-xl border border-border/70 bg-background/60 p-3">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Recent IDs
                    <select
                      multiple
                      value={selectedHistory}
                      onChange={handleHistorySelection}
                      size={Math.min(Math.max(history.length, 6), 12)}
                      className="mt-2 w-full rounded-lg border border-border/70 bg-background/70 p-2 text-sm text-foreground custom-scrollbar"
                      disabled={state.loading}
                    >
                      {history.map((entry) => {
                        const remote = serverHistoryMap.get(entry);
                        const label =
                          remote?.artist_name && remote.artist_name !== entry
                            ? `${remote.artist_name} (${entry})`
                            : entry;
                        return (
                          <option key={entry} value={entry}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground-200 transition hover:bg-background/30 disabled:opacity-40"
                    onClick={handleAddFromHistory}
                    disabled={!selectedHistory.length || state.loading}
                  >
                    Add selected
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end">
              <button
                type="submit"
                className="rounded-full border border-amber-500/60 px-4 py-2 text-sm font-semibold text-primary-foreground dark:text-amber-200 transition hover:bg-amber-500/10 disabled:opacity-40"
                disabled={runDisabled}
              >
                {state.loading ? "Fetching…" : "Run digest"}
              </button>
            </div>
          </form>

          {state.error && <p className="text-sm text-rose-300">{state.error}</p>}
          {state.serverErrors.length > 0 && (
            <ul className="text-sm text-rose-200">
              {state.serverErrors.map((err, idx) => (
                <li key={`${err}-${idx}`}>{err}</li>
              ))}
            </ul>
          )}

          <div className="space-y-4">
            <div className="overflow-x-auto rounded-2xl border border-border/70">
              {state.venues.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground/80">
                  {state.loading ? "Loading venues…" : "No venues yet. Run a digest to populate this table."}
                </p>
              ) : (
                <table className="w-full min-w-[720px] text-sm text-foreground/90">
                  <thead className="bg-background/70 text-xs uppercase tracking-wide text-muted-foreground/80">
                    <tr>
                      <th className="px-3 py-2 text-left">
                        <label className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center md:min-h-[32px] md:min-w-[32px]">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleSelectAll}
                            aria-label="Select all venues"
                            className="h-5 w-5 rounded border border-border bg-background/70 md:h-4 md:w-4"
                          />
                          <span className="sr-only">Select all venues</span>
                        </label>
                      </th>
                      <th className="px-3 py-2 text-left">Venue</th>
                      <th className="px-3 py-2 text-left">Location</th>
                      <th className="px-3 py-2 text-left">Upcoming</th>
                      <th className="px-3 py-2 text-left">Past</th>
                      <th className="px-3 py-2 text-left">Latest Event</th>
                      <th className="px-3 py-2 text-left">Tickets</th>
                      <th className="px-3 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.venues.map((venue) => {
                      const latest = venue.latest_event_date ? formatDate(venue.latest_event_date) : "—";
                      const isActive = venue.digest_id === selectedDigestId;
                      const override = overrides[venue.digest_id] ?? {};
                      return (
                        <Fragment key={venue.digest_id}>
                          <tr
                            onClick={() => setSelectedDigestId(venue.digest_id)}
                            className={`border-t border-border/80 ${isActive ? "bg-background/30" : ""} cursor-pointer`}
                          >
                            <td className="px-3 py-3 align-top">
                              <label className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center md:min-h-[32px] md:min-w-[32px]">
                                <input
                                  type="checkbox"
                                  checked={selectedRows.has(venue.digest_id)}
                                  onChange={() => toggleSelection(venue.digest_id)}
                                  aria-label={`Select ${venue.venue_name}`}
                                  className="h-5 w-5 rounded border border-border bg-background/70 md:h-4 md:w-4"
                                />
                                <span className="sr-only">Select venue</span>
                              </label>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div>
                                <p className="font-semibold text-foreground">{venue.venue_name}</p>
                                {venue.events.slice(0, 3).map((event, index) => (
                                  <p key={`${venue.digest_id}-event-${index}`} className="text-xs text-muted-foreground">
                                    {event.event_date ? formatDate(event.event_date) : "Date TBA"} · {event.is_past ? "Past" : "Upcoming"}
                                  </p>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-3 align-top text-foreground/70">
                              {[venue.city, venue.state, venue.country].filter(Boolean).join(", ") || "TBA"}
                            </td>
                            <td className="px-3 py-3 align-top">
                              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-200">
                                {venue.upcoming_events}
                              </span>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <span className="rounded-full bg-muted/40 px-2 py-0.5 text-xs font-semibold text-foreground/80">
                                {venue.past_events}
                              </span>
                            </td>
                            <td className="px-3 py-3 align-top text-foreground/70">{latest}</td>
                            <td className="px-3 py-3 align-top text-primary-foreground dark:text-amber-200">
                              {venue.ticket_url ? (
                                <a href={venue.ticket_url} target="_blank" rel="noreferrer" className="underline">
                                  View link ↗
                                </a>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-3 py-3 align-top">
                              <button
                                type="button"
                                className="rounded-full border border-rose-500/40 px-3 py-1 text-xs text-rose-200 transition hover:bg-rose-500/10"
                                onClick={() => handleDismissVenue(venue.digest_id)}
                              >
                                Dismiss
                              </button>
                            </td>
                          </tr>
                          {isActive ? (
                            <tr className="border-t border-border/70 bg-background/20">
                              <td colSpan={8} className="px-4 py-4">
                                <div className="grid gap-3 md:grid-cols-2">
                                  <InputField
                                    label="Contact name"
                                    value={override.contact_name ?? ""}
                                    onChange={(value) => setOverrideField(venue.digest_id, "contact_name", value)}
                                  />
                                  <InputField
                                    label="Email"
                                    type="email"
                                    value={override.email ?? ""}
                                    onChange={(value) => setOverrideField(venue.digest_id, "email", value)}
                                  />
                                  <InputField
                                    label="Phone"
                                    type="tel"
                                    value={override.phone ?? ""}
                                    onChange={(value) => setOverrideField(venue.digest_id, "phone", value)}
                                  />
                                  <InputField
                                    label="Website"
                                    type="url"
                                    value={override.website ?? ""}
                                    onChange={(value) => setOverrideField(venue.digest_id, "website", value)}
                                  />
                                </div>
                                <label className="mt-3 block text-xs text-muted-foreground">
                                  Notes
                                  <textarea
                                    rows={3}
                                    value={override.notes ?? ""}
                                    onChange={(event) =>
                                      setOverrideField(venue.digest_id, "notes", event.target.value)
                                    }
                                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-sm text-foreground"
                                  />
                                </label>
                                <p className="mt-2 text-[11px] text-muted-foreground">
                                  Inline edits are stored for this venue and will be applied on approval.
                                </p>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="flex flex-col gap-3 rounded-2xl border border-border/70 bg-background/40 p-4">
              <label className="text-xs text-muted-foreground">
                Tags
                <input
                  type="text"
                  value={tags.join(", ")}
                  onChange={(event) => handleTagChange(event.target.value)}
                  placeholder="bandsintown, digest"
                  className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-sm text-foreground"
                />
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  {activeVenue
                    ? "Review inline edits before approving selected venues."
                    : "Select a venue row to add contact overrides before sending."}
                </p>
                <button
                  type="button"
                  className="flex items-center justify-center rounded-full border border-amber-500/60 bg-amber-500/20 px-4 py-2 text-xs font-semibold text-primary-foreground dark:text-amber-200 transition hover:bg-amber-500/30 disabled:border-border disabled:text-muted-foreground"
                  onClick={handleSend}
                  disabled={sending || selectedVenues.length === 0}
                >
                  {sending
                    ? "Sending…"
                    : selectedVenues.length
                    ? `Send selected (${selectedVenues.length})`
                    : "Select venues to send"}
                </button>
              </div>
              {sendStatus.success && (
                <p className="text-xs text-emerald-200">{sendStatus.success}</p>
              )}
              {sendStatus.error && (
                <p className="text-xs text-rose-300">{sendStatus.error}</p>
              )}
            </div>
          </div>
        </div>
      </CapabilityGate>
    </div>
  );
}
